import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import DepotController from './controllers/depot-controller';
import Modal from '../../components/Modal';
import { usePermissionViewModel } from './permissions/viewmodel/permission-viewmodel';

const emptyDepot = { name: '', adresse: '', isDefault: false };

const DepotsPage = () => {
  const { hasPermission } = usePermissionViewModel('depot');
  const canWrite = hasPermission('write');
  const canEdit = hasPermission('edit');
  const canDelete = hasPermission('delete');
  const [loading, setLoading] = useState(true);
  const [depots, setDepots] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [maxPage, setMaxPage] = useState(1);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [formData, setFormData] = useState(emptyDepot);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchDepots = async () => {
    setLoading(true);
    try {
      const response = await DepotController.fetchDepots(currentPage, pageSize, searchTerm);
      setDepots(response.list || []);
      setTotalItems(response.total || 0);
      setMaxPage(response.maxPage || 1);
    } catch (e) {
      console.error(e);
      toast.error('Erreur lors du chargement des dépôts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDepots(); }, [currentPage, searchTerm]);

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Le nom du dépôt est requis');
      return;
    }
    setSubmitting(true);
    try {
      await DepotController.createDepot({
        name: formData.name.trim(),
        adresse: formData.adresse?.trim() || '',
        isDefault: !!formData.isDefault,
      });
      toast.success('Dépôt créé et stock initialisé');
      setIsCreateOpen(false);
      setFormData(emptyDepot);
      fetchDepots();
    } catch (e) {
      console.error(e);
      toast.error('Erreur lors de la création du dépôt');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async () => {
    if (!formData.name.trim()) {
      toast.error('Le nom du dépôt est requis');
      return;
    }
    setSubmitting(true);
    try {
      await DepotController.updateDepot({
        id: editing.id,
        name: formData.name.trim(),
        adresse: formData.adresse?.trim() || '',
        isDefault: !!formData.isDefault,
      });
      toast.success('Dépôt mis à jour');
      setIsEditOpen(false);
      setEditing(null);
      fetchDepots();
    } catch (e) {
      console.error(e);
      toast.error('Erreur lors de la mise à jour du dépôt');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    setSubmitting(true);
    try {
      await DepotController.deleteDepot(toDelete.id);
      toast.success('Dépôt supprimé');
      setIsDeleteOpen(false);
      setToDelete(null);
      fetchDepots();
    } catch (e) {
      console.error(e);
      toast.error('Erreur lors de la suppression du dépôt');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="py-6 px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dépôts</h1>
          <p className="mt-2 text-sm text-gray-700">
            Gérer les dépôts. Ajouter un nouveau dépôt initialise automatiquement le stock à 0 pour chaque variante de produit.
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            type="button"
            onClick={() => { setFormData(emptyDepot); setIsCreateOpen(true); }}
            disabled={!canWrite}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md
              shadow-sm text-sm font-medium text-white bg-[#174540] hover:bg-[#0d2926] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            + Nouveau Dépôt
          </button>
        </div>
      </div>

      <div className="mt-6">
        <input
          type="text"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm sm:text-sm"
          placeholder="Rechercher un dépôt par nom..."
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
        />
      </div>

      <div className="mt-6 bg-white shadow overflow-hidden sm:rounded-md">
        {loading ? (
          <div className="p-12 flex justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#174540]" />
          </div>
        ) : depots.length === 0 ? (
          <div className="p-12 text-center text-gray-500">Aucun dépôt trouvé.</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Adresse</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Par défaut</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {depots.map((d) => (
                <tr key={d.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{d.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-700">{d.adresse || '—'}</td>
                  <td className="px-6 py-4 text-sm">
                    {d.isDefault ? (
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        Par défaut
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                    {canEdit && (
                      <button
                        onClick={() => { setEditing(d); setFormData({ name: d.name, adresse: d.adresse || '', isDefault: !!d.isDefault }); setIsEditOpen(true); }}
                        className="text-[#174540] hover:underline"
                      >
                        Éditer
                      </button>
                    )}
                    {canDelete && (
                      <button
                        onClick={() => { setToDelete(d); setIsDeleteOpen(true); }}
                        className="text-red-600 hover:underline"
                      >
                        Supprimer
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {maxPage > 1 && (
        <div className="mt-4 flex justify-between items-center">
          <span className="text-sm text-gray-600">
            {totalItems} dépôt(s) — Page {currentPage} / {maxPage}
          </span>
          <div className="space-x-2">
            <button
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage(currentPage - 1)}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Précédent
            </button>
            <button
              disabled={currentPage >= maxPage}
              onClick={() => setCurrentPage(currentPage + 1)}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Suivant
            </button>
          </div>
        </div>
      )}

      {/* Create Modal */}
      <Modal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        title="Nouveau Dépôt"
        onSubmit={handleCreate}
        submitLabel="Créer"
        isSubmitting={submitting}
      >
        <DepotForm formData={formData} setFormData={setFormData} />
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title="Modifier le Dépôt"
        onSubmit={handleEdit}
        submitLabel="Enregistrer"
        isSubmitting={submitting}
      >
        <DepotForm formData={formData} setFormData={setFormData} />
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        title="Supprimer le Dépôt"
        onSubmit={handleDelete}
        submitLabel="Supprimer"
        submitColor="red"
        isSubmitting={submitting}
      >
        <p className="py-4 text-gray-700">
          Êtes-vous sûr de vouloir supprimer le dépôt <strong>{toDelete?.name}</strong> ?
          Tous les stocks associés à ce dépôt seront également supprimés.
        </p>
      </Modal>
    </div>
  );
};

const DepotForm = ({ formData, setFormData }) => (
  <div className="space-y-4">
    <div>
      <label className="block text-sm font-medium text-gray-700">Nom *</label>
      <input
        type="text"
        value={formData.name}
        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm"
        placeholder="Nom du dépôt"
        autoFocus
        required
      />
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700">Adresse</label>
      <input
        type="text"
        value={formData.adresse}
        onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm"
        placeholder="Adresse du dépôt"
      />
    </div>
    <div className="flex items-center">
      <input
        id="isDefault"
        type="checkbox"
        checked={!!formData.isDefault}
        onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
        className="h-4 w-4 text-[#174540] border-gray-300 rounded"
      />
      <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-700">
        Définir comme dépôt par défaut
      </label>
    </div>
  </div>
);

export default DepotsPage;
